import { humanize } from './auditReviewView';

const ADDITIONAL_NOTES = 'additional_notes';

export const isV2Score = (items) =>
  (items || []).some((item) => String(item?.category || '').trim());

export const hasScoreCategory = (row) =>
  Boolean(String(row?.category || '').trim() || String(row?.category_label || '').trim());

/** v1 heat-map rows. A categorized leaf stays on the v2 checklist. */
export const v1WorkOnItems = (items) =>
  (items || []).filter((item) => item?.item_id && !String(item?.category || '').trim());

/** Summary groups to render. Additional Notes is never a summary row. */
export const summaryChecklist = (groups) =>
  (groups || []).filter(
    (group) =>
      group?.category !== ADDITIONAL_NOTES && (group.items || []).some((item) => item?.item_id)
  );

/** The check in a category with the highest finding rate. Ties keep tree order. */
export const categoryHeatCell = (items) => {
  const scored = (items || []).filter(
    (item) => item?.item_id && Number.isFinite(Number(item.fail_pct))
  );
  if (!scored.length) return null;
  return scored.reduce((worst, item) =>
    Number(item.fail_pct) > Number(worst.fail_pct) ? item : worst
  );
};

export const summaryItemTitle = (row) => {
  const label = String(row?.label || '').trim();
  const categoryLabel = String(row?.category_label || '').trim();
  const subcategoryLabel = String(row?.subcategory_label || '').trim();
  if (!hasScoreCategory(row)) return row?.issue_label || 'Checklist item';
  const parts = [];
  if (categoryLabel && categoryLabel !== label) parts.push(categoryLabel);
  if (subcategoryLabel && subcategoryLabel !== label && subcategoryLabel !== categoryLabel) {
    parts.push(subcategoryLabel);
  }
  if (label) parts.push(label);
  if (!parts.length) return row?.issue_label || 'Checklist item';
  return parts.join(' / ');
};

/** Consecutive checklist rows, with a subcategory heading when the API sent one. */
export const checklistSections = (items) => {
  const sections = [];
  (items || [])
    .filter((item) => item?.item_id)
    .forEach((item) => {
      const key = String(item.subcategory || '').trim();
      const heading = key ? String(item.subcategory_label || '').trim() : '';
      const last = sections[sections.length - 1];
      if (last && last.key === key) {
        last.items.push(item);
        return;
      }
      sections.push({ key, heading, items: [item] });
    });
  return sections;
};

export const isHiddenRecommendation = (finding) => {
  const fieldId = String(finding?.field_id || '');
  if (!fieldId.startsWith('recommendation_')) return false;
  const status = finding?.status || '';
  return status === 'na' || status === 'not_applicable';
};

export const v2StatusLabel = (leaf) => {
  if (!leaf || !leaf.scored) return '';
  if (leaf.status === 'pass') return 'Passed';
  if (leaf.status === 'fail') return 'Correction needed';
  if (leaf.status === 'na') return 'Not scored';
  if (leaf.status === 'pending') return 'Pending';
  return humanize(leaf.status);
};

export const v2LeafDescription = (leaf) => {
  if (!leaf) return '';
  if (leaf.issueLabel) return leaf.issueLabel;
  if (!leaf.scored) return '';
  if (leaf.status === 'pass') return 'This item passed.';
  if (leaf.status === 'na') return 'This item is not scored.';
  if (leaf.status === 'pending') return 'This item is pending.';
  return '';
};

/** Sidebar dot. Empty when the row is not a scored leaf. Pending and na are not failures. */
export const v2Dot = (leaf) => {
  if (!leaf?.scored) return '';
  if (leaf.status === 'pass') return 'pass';
  if (leaf.status === 'fail') return 'attention';
  return 'na';
};

/** Category dot: green when every check passed, red when any check failed. */
export const categorySummaryDot = (leaves) => {
  const scored = (leaves || []).filter((leaf) => leaf.scored);
  if (!scored.length) return '';
  if (scored.some((leaf) => leaf.status === 'fail')) return 'fail';
  if (scored.every((leaf) => leaf.status === 'pass')) return 'pass';
  return 'na';
};

/** One accordion per major category. Notes and Other stay loose rows. */
export const checklistAccordions = (nodes) => {
  const sections = [];
  (nodes || []).forEach((node) => {
    const last = sections[sections.length - 1];
    if (last && last.groupKey === node.groupKey) {
      last.nodes.push(node);
      if (node.type === 'leaf') last.leaves.push(node);
      return;
    }
    sections.push({
      groupKey: node.groupKey,
      heading: node.type === 'heading' && !node.depth ? node : null,
      nodes: [node],
      leaves: node.type === 'leaf' ? [node] : [],
    });
  });
  return sections;
};

const scoredLeaf = (item, depth) => ({
  type: 'leaf',
  key: item.item_id,
  name: item.label || '',
  status: item.status || '',
  issueLabel: item.issue_label || '',
  findings: [],
  additional: false,
  scored: true,
  depth,
});

const unscoredLeaf = (key, name, findings) => ({
  type: 'leaf',
  key,
  name,
  status: '',
  issueLabel: '',
  findings,
  additional: true,
  scored: false,
  depth: 0,
});

/**
 * Group v2 grading checks in API order under category and subcategory labels.
 * Additional Notes, then Other, sit after the scored checklist.
 */
export const buildV2Checklist = (items, findings) => {
  const groups = [];
  (items || []).forEach((item) => {
    const category = String(item?.category || '').trim();
    const subcategory = String(item?.subcategory || '').trim();
    const subcategoryLabel = String(item?.subcategory_label || '').trim();
    const leaf = scoredLeaf(item, subcategory && subcategoryLabel ? 2 : 1);
    const groupKey = category || item.item_id;
    let group = groups[groups.length - 1];
    if (!group || group.key !== groupKey) {
      group = {
        key: groupKey,
        heading: item.category_label || '',
        sections: [],
      };
      groups.push(group);
    }
    let section = group.sections[group.sections.length - 1];
    if (!section || section.key !== subcategory) {
      section = {
        key: subcategory,
        heading: subcategory && subcategoryLabel ? subcategoryLabel : '',
        leaves: [],
      };
      group.sections.push(section);
    }
    section.leaves.push(leaf);
  });

  const source = (findings || []).filter((finding) => !isHiddenRecommendation(finding));
  const itemsById = new Map((items || []).map((item) => [item.item_id, item]));
  const linked = new Set();
  groups.forEach((group) => {
    group.sections.forEach((section) => {
      section.leaves.forEach((leaf) => {
        const ids = new Set(itemsById.get(leaf.key)?.finding_ids || []);
        const rows = source.filter((finding) => ids.has(finding.finding_id));
        rows.forEach((finding) => linked.add(finding.finding_id));
        leaf.findings = rows;
      });
    });
  });

  const unmatched = source.filter((finding) => !linked.has(finding.finding_id));
  const notes = unmatched.filter((finding) => finding.section === ADDITIONAL_NOTES);
  const other = unmatched.filter((finding) => finding.section !== ADDITIONAL_NOTES);
  const extras = [];
  if (notes.length) {
    extras.push({
      key: '__additional_notes',
      heading: '',
      sections: [
        { key: '', heading: '', leaves: [unscoredLeaf('__additional_notes', 'Additional Notes', notes)] },
      ],
    });
  }
  if (other.length) {
    extras.push({
      key: '__other',
      heading: '',
      sections: [{ key: '', heading: '', leaves: [unscoredLeaf('__other', 'Other', other)] }],
    });
  }
  groups.push(...extras);

  const nodes = [];
  groups.forEach((group) => {
    if (group.heading) {
      nodes.push({
        type: 'heading',
        key: `heading-${group.key}-${nodes.length}`,
        name: group.heading,
        depth: 0,
        groupKey: group.key,
      });
    }
    group.sections.forEach((section) => {
      if (section.heading) {
        nodes.push({
          type: 'heading',
          key: `sub-${group.key}-${section.key}-${nodes.length}`,
          name: section.heading,
          depth: 1,
          groupKey: group.key,
        });
      }
      section.leaves.forEach((leaf) => {
        leaf.groupKey = group.key;
        nodes.push(leaf);
      });
    });
  });
  return {
    nodes,
    leaves: nodes.filter((node) => node.type === 'leaf'),
  };
};
